// StockDetailPage.js - 수정버전
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { CircleChevronRight, ArrowLeft, Star, ChevronRight, ChevronLeft, BarChart2, TrendingUp, FileText, MessageSquare, Calendar, Search, Settings, ArrowUp, Brain, Newspaper, DollarSign, Info, Zap, AlertTriangle, Languages, Mail, Globe, Twitter, Users } from 'lucide-react';
import SearchModal from './SearchModal/SearchModal'; // 새로운 SearchModal 컴포넌트 import
import { AnimatedCircularProgress } from './Progress/AnimatedCircularProgress';
import { TradingJournal } from './Journal/TradingJournal'; // Trading Journal 컴포넌트 import
import FullAnalysisModal from './FullAnalysisModal/FullAnalysisModal';
import DetailTradingView from './TradingView/DetailTradingView'; // 경로에 맞게 수정
import InvestmentDisclaimer from './Common/InvestmentDisclaimer';
import ContactFooter from './Common/ContactFooter';
import {
  getInvestmentRecommendation,
  getNewsAnalysis,
  getRiskAnalysis,
  getSentimentAnalysis,
  getTechnicalAnalysis,
  getFundamentalAnalysis,
  getStockDetailFundamental,
  getStockDetailTechnical,
  getStockDetailNews,
  getStockDetailSummary,
  getStockDetailCommunity,
  getStockScoreHistory,
  getShortPositionAnalysis,
  toggleFavorite,
  getFavoriteList,
  getRefreshToken,
  isOAuthUser,
  getLastRunDate,
  getPlatform
} from '../services/api';
import './StockDetailPage.css';
import AlertModal, { useAlert } from './Common/Alert';

const StockDetailPage = () => {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isFullAnalysisModalOpen, setIsFullAnalysisModalOpen] = useState(false);
  const [currentReportType, setCurrentReportType] = useState("investment");
  const [investmentData, setInvestmentData] = useState(null);
  const [newsData, setNewsData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [sentimentData, setSentimentData] = useState(null);
  const [technicalData, setTechnicalData] = useState(null);
  const [fundamentalData, setFundamentalData] = useState(null);
  const [stockDetailFundamental, setStockDetailFundamental] = useState(null);
  const [stockDetailTechnical, setStockDetailTechnical] = useState(null);
  const [stockDetailNews, setStockDetailNews] = useState(null);
  const [stockDetailSummary, setStockDetailSummary] = useState(null);
  const [stockDetailCommunity, setStockDetailCommunity] = useState(null);
  const [stockScoreHistory, setStockScoreHistory] = useState([]);
  const [shortPositionData, setShortPositionData] = useState(null);
  const [communityCurrentPage, setCommunityCurrentPage] = useState(1);
  const [newsCurrentPage, setNewsCurrentPage] = useState(1);
  const [showTooltip, setShowTooltip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const { alert, showAlert, closeAlert, showAuthAlert } = useAlert();


  // 앱 환경 감지 및 링크 처리 함수
  const handleExternalLink = (url) => {
    const device = getPlatform();
    // Android WebView에서 JavaScript Interface 확인
    if (device === 'android') {
      window.openExternalUrl.postMessage(url);
      return;
    }

    // iOS WebView에서 JavaScript Interface 확인
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.openExternalUrl) {
      window.webkit.messageHandlers.openExternalUrl.postMessage(url);
      return;
    }

    // 모바일 환경에서 외부 앱으로 열기
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      // 모바일에서는 같은 탭에서 열기 (앱 내 브라우저)
      window.location.href = url;
    } else {
      // 데스크톱에서는 새 탭에서 열기
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // 구글 번역 링크 생성 및 열기
  const handleTranslate = (text) => {
    // 구글 번역 URL 생성 (영어 -> 한국어)
    const translateUrl = `https://translate.google.com/?sl=en&tl=ko&text=${encodeURIComponent(text)}&op=translate`;
    handleExternalLink(translateUrl);
  };
  
  const { stockName } = useParams();
  const navigate = useNavigate();

  // stockName 변경 감지 및 초기화
  useEffect(() => {
    console.log('StockDetailPage: stockName changed to:', stockName);
    
    // 페이지 상태 초기화
    setCommunityCurrentPage(1);
    setNewsCurrentPage(1);
    
    // 상단으로 스크롤
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stockName]);

  // TradingView symbol 포맷 변환 함수\
  const handleBack = () => {
    navigate(-1);
  };

  const handleStockClick = (ticker) => {
    navigate(`/stockDetail/${ticker}/`);
  };

  const openSearchModal = (e) => {
    // 이벤트 객체가 있는 경우 즉시 기본 동작 방지
    console.log(e, 'Search modal open triggered');
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent?.preventDefault();
      e.nativeEvent?.stopImmediatePropagation();
    }
    
    // 즉시 상태 업데이트 (setTimeout 제거)
    setIsSearchModalOpen(true);
    
    return false; // 추가적인 기본 동작 방지
  };

  const closeSearchModal = () => {
    setIsSearchModalOpen(false);
  };


  // 즐겨찾기 상태 확인 함수
  const checkFavoriteStatus = async () => {
    if (!stockName) return;
    
    try {
      const favoriteList = await getFavoriteList();
      const favoriteItem = favoriteList.list?.find(item => item.ticker === stockName);
      
      if (favoriteItem) {
        setIsFavorite(true);
      } else {
        setIsFavorite(false);
      }
    } catch (error) {
      console.error('Error checking favorite status:', error);
      setIsFavorite(false);
    }
  };

  // 전체 분석 보기 클릭 핸들러 (토큰 체크)
  const handleKakaoShare = (sectionTitle, summary) => {
    const kakao = window.Kakao;
    if (!kakao) return;
    if (!kakao.isInitialized()) {
      kakao.init('3a612caf3635160cef3ec4542c0b8344');
    }
    const cleanSummary = summary
      ? summary.replace(/[#*_~`>]/g, '').trim().slice(0, 100) + '...'
      : `${stockName} AI 분석을 확인하세요.`;
    kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: `투자특공대 · ${stockName} ${sectionTitle}`,
        description: cleanSummary,
        imageUrl: `${window.location.origin}/social-share.png`,
        link: {
          webUrl: 'https://mmstudio.co.kr',
          mobileWebUrl: 'https://mmstudio.co.kr/appStore',
        },
      },
      buttons: [{
        title: '앱에서 보기',
        link: {
          webUrl: 'https://mmstudio.co.kr',
          mobileWebUrl: 'https://mmstudio.co.kr/appStore',
          androidExecutionParams: `ticker=${stockName}`,
          iosExecutionParams: `ticker=${stockName}`,
        },
      }],
    });
  };

  const handleFullAnalysisClick = (reportType) => {
    const token = getRefreshToken();
    if (!token) {
      showAuthAlert();
      return;
    }
    setCurrentReportType(reportType);
    setIsFullAnalysisModalOpen(true);
  };

  // 즐겨찾기 추가/제거 함수
  const handleFavoriteToggle = async () => {
    if (!stockName) return;

    setFavoriteLoading(true);

    try {
      if (!isOAuthUser()) {
        showAuthAlert();
        return;
      }

      const newFavoriteState = !isFavorite;
      const result = await toggleFavorite(stockName, newFavoriteState);
      console.log('Favorite toggled:', result);
      setIsFavorite(newFavoriteState);

    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      if (!stockName) return;

      try {
        setLoading(true);
        setError(null);

        // Get last run date for this ticker
        const runDate = await getLastRunDate(stockName);

        // Fetch all data in parallel
        const [
          investmentResponse,
          newsResponse,
          riskResponse,
          sentimentResponse,
          technicalResponse,
          fundamentalResponse,
          stockDetailFundamentalResponse,
          stockDetailTechnicalResponse,
          stockDetailNewsResponse,
          stockDetailSummaryResponse,
          stockDetailCommunityResponse,
          shortPositionResponse,
          stockScoreHistoryResponse
        ] = await Promise.allSettled([
          getInvestmentRecommendation(stockName, runDate),
          getNewsAnalysis(stockName, runDate),
          getRiskAnalysis(stockName, runDate),
          getSentimentAnalysis(stockName, runDate),
          getTechnicalAnalysis(stockName, runDate),
          getFundamentalAnalysis(stockName, runDate),
          getStockDetailFundamental(stockName, runDate),
          getStockDetailTechnical(stockName, runDate),
          getStockDetailNews(stockName, runDate),
          getStockDetailSummary(stockName, runDate),
          getStockDetailCommunity(stockName, runDate),
          getShortPositionAnalysis(stockName, runDate),
          getStockScoreHistory(stockName)
        ]);

        // Set data for successful requests
        if (investmentResponse.status === 'fulfilled') {
          setInvestmentData(investmentResponse.value);
        }
        if (newsResponse.status === 'fulfilled') {
          setNewsData(newsResponse.value);
        }
        if (riskResponse.status === 'fulfilled') {
          setRiskData(riskResponse.value);
        }
        if (sentimentResponse.status === 'fulfilled') {
          setSentimentData(sentimentResponse.value);
        }
        if (technicalResponse.status === 'fulfilled') {
          setTechnicalData(technicalResponse.value);
        }
        if (fundamentalResponse.status === 'fulfilled') {
          setFundamentalData(fundamentalResponse.value);
        }
        if (stockDetailFundamentalResponse.status === 'fulfilled') {
          setStockDetailFundamental(stockDetailFundamentalResponse.value);
        }
        if (stockDetailTechnicalResponse.status === 'fulfilled') {
          setStockDetailTechnical(stockDetailTechnicalResponse.value);
        }

       if (stockDetailNewsResponse.status === 'fulfilled') {
          setStockDetailNews(stockDetailNewsResponse.value);
       }
       if (stockDetailSummaryResponse.status === 'fulfilled') {
          setStockDetailSummary(stockDetailSummaryResponse.value);
       }
       if (stockDetailCommunityResponse.status === 'fulfilled') {
          setStockDetailCommunity(stockDetailCommunityResponse.value);
       }
       if (shortPositionResponse.status === 'fulfilled') {
          setShortPositionData(shortPositionResponse.value);
       }
       if (stockScoreHistoryResponse.status === 'fulfilled') {
          setStockScoreHistory(stockScoreHistoryResponse.value?.list || []);
       }
        // Check for critical failures
        const responses = [investmentResponse, newsResponse, riskResponse, sentimentResponse, technicalResponse, fundamentalResponse, stockDetailFundamentalResponse, stockDetailTechnicalResponse, stockDetailNewsResponse, stockDetailSummaryResponse, stockDetailCommunityResponse, shortPositionResponse];
        const apiNames = ['investment', 'news', 'risk', 'sentiment', 'technical', 'fundamental', 'stockDetailFundamental', 'stockDetailTechnical', 'stockDetailNews', 'stockDetailSummary', 'stockDetailCommunity', 'shortPosition'];
        
        let failedCount = 0;
        responses.forEach((response, index) => {
          if (response.status === 'rejected') {
            failedCount++;
            console.error(`Failed to fetch ${apiNames[index]} data:`, response.reason);
            
            // Check for 500 errors specifically
            if (response.reason?.message?.includes('500') || response.reason?.message?.includes('Internal Server Error')) {
              console.error(`Server error (500) for ${apiNames[index]}:`, response.reason);
            }
          }
        });
        
        // If more than half of the APIs failed, show error page
        // TEMPORARILY DISABLED FOR DEVELOPMENT - CORS issues
        // if (failedCount > responses.length / 2) {
        //   setError(`주요 데이터를 불러오는 중 문제가 발생했습니다. (실패한 API: ${failedCount}/${responses.length})`);
        //   return;
        // }
        
        // Show warning but don't block page rendering
        if (failedCount > 0) {
          console.warn(`Warning: ${failedCount}/${responses.length} APIs failed, but continuing to render page`);
        }
        
      } catch (err) {
        setError(err.message);
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [stockName]);

  // 컴포넌트 마운트 시 인증 초기화
  useEffect(() => {
    const initializeAuth = async () => {
      if (isOAuthUser()) {
        await checkFavoriteStatus();
      }
    };

    initializeAuth();
  }, [stockName]);

  // 커뮤니티 페이지네이션 관련 상수 및 함수
  const COMMUNITY_POSTS_PER_PAGE = 3;
  
  const getCommunityPaginatedPosts = () => {
    if (!stockDetailCommunity?.communityDataDto) return [];
    const startIndex = (communityCurrentPage - 1) * COMMUNITY_POSTS_PER_PAGE;
    const endIndex = startIndex + COMMUNITY_POSTS_PER_PAGE;
    return stockDetailCommunity.communityDataDto.slice(startIndex, endIndex);
  };

  const getCommunityTotalPages = () => {
    if (!stockDetailCommunity?.communityDataDto) return 0;
    return Math.ceil(stockDetailCommunity.communityDataDto.length / COMMUNITY_POSTS_PER_PAGE);
  };

  const handleCommunityPageChange = (page) => {
    setCommunityCurrentPage(page);
  };

  // 뉴스 페이지네이션 관련 상수 및 함수
  const NEWS_POSTS_PER_PAGE = 3;
  
  const getNewsPaginatedPosts = () => {
    if (!stockDetailNews?.newsData?.parsed_news) return [];
    const startIndex = (newsCurrentPage - 1) * NEWS_POSTS_PER_PAGE;
    const endIndex = startIndex + NEWS_POSTS_PER_PAGE;
    return stockDetailNews.newsData.parsed_news.slice(startIndex, endIndex);
  };

  const getNewsTotalPages = () => {
    if (!stockDetailNews?.newsData?.parsed_news) return 0;
    return Math.ceil(stockDetailNews.newsData.parsed_news.length / NEWS_POSTS_PER_PAGE);
  };

  const handleNewsPageChange = (page) => {
    setNewsCurrentPage(page);
  };

  // 페이지네이션 번호 생성 함수 (축약형)
  const generatePageNumbers = (currentPage, totalPages) => {
    const pages = [];
    
    if (totalPages <= 7) {
      // 페이지가 7개 이하면 모든 페이지 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 페이지가 많을 때 축약형 적용
      if (currentPage <= 4) {
        // 현재 페이지가 앞쪽에 있을 때: 1 2 3 4 5 ... 11
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        // 현재 페이지가 뒤쪽에 있을 때: 1 ... 7 8 9 10 11
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        // 현재 페이지가 중간에 있을 때: 1 ... 4 5 6 ... 11
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  // 에러 페이지 렌더링
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          {/* 에러 아이콘 */}
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          
          {/* 에러 메시지 */}
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            데이터를 불러올 수 없습니다
          </h2>
          
          <p className="text-gray-600 mb-2">
            {stockName ? `${stockName} 주식 정보를` : '주식 정보를'} 가져오는 중 문제가 발생했습니다.
          </p>
          
          <p className="text-sm text-gray-500 mb-8">
            잠시 후 다시 시도하거나 메인 페이지로 돌아가 주세요.
          </p>
          
          {/* 버튼들 */}
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              페이지 새로고침
            </button>
            
            {/* <button
              onClick={() => navigate('/')}
              className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              메인 페이지로 돌아가기
            </button> */}
          </div>
          
          {/* 기술적 세부사항 (개발용) */}
          {process.env.NODE_ENV === 'development' && (
            <details className="mt-6 text-left">
              <summary className="text-sm text-gray-500 cursor-pointer">기술적 세부사항</summary>
              <pre className="text-xs text-gray-400 mt-2 bg-gray-50 p-2 rounded overflow-auto max-h-20">
                {error}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="detail-container">
      <header className="header">
        <div className="header-content">
          <div className="header-row">
            <div className="logo-container">
              <button className="back-button" onClick={handleBack}>
                <ArrowLeft size={20} />
              </button>
            </div>
            <div className="header-actions">
              <div 
                className="icon-button" 
                role="button"
                tabIndex={0}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openSearchModal(e);
                }}
                style={{ cursor: 'pointer' }}
              >
                <Search size={16} />
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={handleFavoriteToggle}
                disabled={favoriteLoading}
              >
                <Star
                  size={16}
                  fill={isFavorite ? "#f59e0b" : "none"}
                  className={`transition-colors ${isFavorite ? 'text-yellow-400' : ''} ${favoriteLoading ? 'opacity-50' : ''}`}
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="detail-content">
        {/* Chart 섹션 */}
        
          <div className="chart-container">
            <DetailTradingView symbol={stockName} />
          </div>
        

        {/* AI 분석 섹션 */}
        
        <div className="feature-section">
          <div className="flex items-center justify-between gap-1 mb-1">
            {/* 좌측: 아이콘 + 제목 + 설명글 */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="feature-icon">
                  <Brain size={20} />
                </div>
                <h2 className="text-xm font-bold text-black">AI 분석</h2>
              </div>
              <p className="text-xs text-gray-600 pl-1">
                인공지능이 기술 지표, 뉴스, 시장 심리 데이터를 종합적으로 분석하여 투자 인사이트를 제공합니다.
              </p>
            </div>

            {/* 우측: 스코어 */}
            {loading ? (
              <div className="animate-pulse">
                <div className="w-27 h-27 rounded-full bg-gray-200"></div>
              </div>
            ) : (
              <AnimatedCircularProgress
                key={`investment-${stockName}-${investmentData?.score ?? 0}`}
                score={investmentData?.score || 0}
                label={investmentData?.recommendedPosition || "오류"}
                size={108}
                delay={300}
              />
            )}
          </div>
          
          {loading ? (
            <div className="ai-summary-card loading animate-pulse mb-4">
              <div className="h-3 bg-gray-100 rounded mb-3 w-20"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-2/3"></div>
            </div>
          ) : error ? (
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-4 mb-4">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-red-600 leading-relaxed">
                    데이터를 불러오는 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="ai-summary-card">
              <div className="ai-summary-header">
                <div className="flex items-center gap-1.5">
                  <span className="ai-badge"><Zap size={9} />AI</span>
                  <span className="ai-summary-title">핵심 요약</span>
                </div>
                <button className="kakao-share-btn" onClick={() => handleKakaoShare('투자 추천', investmentData?.summary)} disabled={!investmentData?.summary}>
                  <img src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_medium.png" alt="카카오톡 공유" style={{ width: 14, height: 14 }} />
                  공유
                </button>
              </div>
              <p className="ai-summary-text">{investmentData?.summary || `AI 분석 결과 ${stockName}은...`}</p>
              {investmentData?.hashtag && investmentData.hashtag.length > 0 && (
                <div className="ai-hashtag-row">
                  {investmentData.hashtag.map((kw, i) => (
                    <span key={i} className="ai-hashtag">{kw}</span>
                  ))}
                </div>
              )}
              <button className="ai-full-btn" onClick={() => handleFullAnalysisClick("investment")}>
                <span>전체 분석 보기</span>
                <span className="ai-full-btn-arrow"><ChevronRight size={12} /></span>
              </button>
            </div>
          )}

      <TradingJournal
        stockName={stockName}
        scoreHistory={stockDetailSummary?.scoreHistory || []}
        scoreChartData={stockScoreHistory}
        simulationResults={stockDetailSummary?.simulationResults || []}
        monitoringDayCount={stockDetailSummary?.monitoringDayCount || 0}
        totalProfit={stockDetailSummary?.totalProfit || 0}
      />
        </div>
        

        {/* 기술적 분석 섹션 */}
        
        <div className="feature-section">
          <div className="flex items-center justify-between gap-1 mb-1">
            {/* 좌측: 아이콘 + 제목 + 설명글 */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="feature-icon">
                  <TrendingUp size={20} />
                </div>
                <h2 className="text-xm font-bold text-black">기술적 분석</h2>
              </div>
              <p className="text-xs text-gray-600 pl-1">
                주요 기술 지표를 분석하여 현재 주가의 추세와 향후 방향성을 파악합니다.
              </p>
            </div>

            {/* 우측: 스코어 */}
            {loading ? (
              <div className="animate-pulse">
                <div className="w-27 h-27 rounded-full bg-gray-200"></div>
              </div>
            ) : (
              <AnimatedCircularProgress
                key={`technical-${stockName}-${technicalData?.score ?? 0}`}
                score={technicalData?.score || 0}
                label={technicalData?.recommendedPosition || "오류"}
                size={108}
                delay={300}
              />
            )}
          </div>
          
          {loading ? (
            <div className="ai-summary-card loading animate-pulse mb-4">
              <div className="h-3 bg-gray-100 rounded mb-3 w-20"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-2/3"></div>
            </div>
          ) : (
            <div className="ai-summary-card">
              <div className="ai-summary-header">
                <div className="flex items-center gap-1.5">
                  <span className="ai-badge"><Zap size={9} />AI</span>
                  <span className="ai-summary-title">핵심 요약</span>
                </div>
                <button className="kakao-share-btn" onClick={() => handleKakaoShare('기술적 분석', technicalData?.summary)} disabled={!technicalData?.summary}>
                  <img src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_medium.png" alt="카카오톡 공유" style={{ width: 14, height: 14 }} />
                  공유
                </button>
              </div>
              <p className="ai-summary-text">{technicalData?.summary || `${stockName} 기술적 분석 결과`}</p>
              {technicalData?.hashtag && technicalData.hashtag.length > 0 && (
                <div className="ai-hashtag-row">
                  {technicalData.hashtag.map((kw, i) => (
                    <span key={i} className="ai-hashtag">{kw}</span>
                  ))}
                </div>
              )}
              <button className="ai-full-btn" onClick={() => handleFullAnalysisClick("technical")}>
                <span>전체 분석 보기</span>
                <span className="ai-full-btn-arrow"><ChevronRight size={12} /></span>
              </button>
            </div>
          )}
        
          {loading ? (
            <div className="grid grid-cols-2 gap-2 mt-4 animate-pulse">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="tech-indicator-card">
                  <div className="flex items-center justify-between mb-1">
                    <div className="h-2.5 bg-gray-200 rounded w-14"></div>
                    <div className="h-4 bg-gray-200 rounded w-10"></div>
                  </div>
                  <div className="tech-gauge-bar"><div className="tech-gauge-fill bg-gray-200" style={{width:'55%'}}></div></div>
                  <div className="h-6 bg-gray-200 rounded w-10 mt-2"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 mt-4">
              {/* RSI */}
              {(() => {
                const rsi = stockDetailTechnical?.technicalData?.RSI;
                const ob = rsi > 70, os = rsi < 30;
                const color = ob ? '#ef4444' : os ? '#10b981' : '#B8A7E8';
                const badgeCls = ob ? 'tech-status-overbought' : os ? 'tech-status-oversold' : 'tech-status-neutral';
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label">RSI (14)</span>
                      <span className={`tech-status-badge ${badgeCls}`}>{ob ? '과매수' : os ? '과매도' : '균형'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{width:`${Math.min(Math.max(rsi||0,0),100)}%`, background:color}}></div>
                      <div className="tech-gauge-marker" style={{left:'30%'}}></div>
                      <div className="tech-gauge-marker" style={{left:'70%'}}></div>
                    </div>
                    <div className="flex items-baseline gap-0.5">
                      <span className="tech-indicator-value" style={{color}}>{rsi?.toFixed(1)||'-'}</span>
                      <span className="text-[10px] text-gray-400 font-medium">/100</span>
                    </div>
                  </div>
                );
              })()}
              {/* ADX */}
              {(() => {
                const adx = stockDetailTechnical?.technicalData?.ADX_14;
                const strong = adx > 25;
                const color = strong ? '#3b82f6' : '#9ca3af';
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label">ADX (14)</span>
                      <span className={`tech-status-badge ${strong ? 'tech-status-strong' : 'tech-status-neutral'}`}>{strong ? '강한추세' : '약한추세'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{width:`${Math.min(adx||0,100)}%`, background:color}}></div>
                      <div className="tech-gauge-marker" style={{left:'25%'}}></div>
                    </div>
                    <div className="flex items-baseline gap-0.5">
                      <span className="tech-indicator-value" style={{color}}>{adx?.toFixed(1)||'-'}</span>
                      <span className="text-[10px] text-gray-400 font-medium">/100</span>
                    </div>
                  </div>
                );
              })()}
              {/* CCI */}
              {(() => {
                const cci = stockDetailTechnical?.technicalData?.CCI;
                const ob = cci > 100, os = cci < -100;
                const color = ob ? '#ef4444' : os ? '#10b981' : '#B8A7E8';
                const badgeCls = ob ? 'tech-status-overbought' : os ? 'tech-status-oversold' : 'tech-status-neutral';
                const pct = Math.min(Math.max(((cci||0)+200)/4, 0), 100);
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label">CCI</span>
                      <span className={`tech-status-badge ${badgeCls}`}>{ob ? '과매수' : os ? '과매도' : '중립'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{width:`${pct}%`, background:color}}></div>
                      <div className="tech-gauge-marker" style={{left:'25%'}}></div>
                      <div className="tech-gauge-marker" style={{left:'50%'}}></div>
                      <div className="tech-gauge-marker" style={{left:'75%'}}></div>
                    </div>
                    <span className="tech-indicator-value" style={{color}}>{cci?.toFixed(0)||'-'}</span>
                  </div>
                );
              })()}
              {/* Williams %R */}
              {(() => {
                const wr = stockDetailTechnical?.technicalData?.Williams_R;
                const ob = wr > -20, os = wr < -80;
                const color = ob ? '#ef4444' : os ? '#10b981' : '#B8A7E8';
                const badgeCls = ob ? 'tech-status-overbought' : os ? 'tech-status-oversold' : 'tech-status-neutral';
                const pct = Math.min(Math.max((wr||0)+100, 0), 100);
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label">Williams %R</span>
                      <span className={`tech-status-badge ${badgeCls}`}>{ob ? '과매수' : os ? '과매도' : '중립'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{width:`${pct}%`, background:color}}></div>
                      <div className="tech-gauge-marker" style={{left:'20%'}}></div>
                      <div className="tech-gauge-marker" style={{left:'80%'}}></div>
                    </div>
                    <span className="tech-indicator-value" style={{color}}>{wr?.toFixed(1)||'-'}</span>
                  </div>
                );
              })()}
            </div>
          )}

          {loading ? (
            <div className="card mt-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-20 mb-3"></div>
              <div className="space-y-1">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="sr-level-row">
                    <div className="h-3 bg-gray-200 rounded w-8"></div>
                    <div className="h-3 bg-gray-200 rounded w-10"></div>
                    <div className="h-3 bg-gray-200 rounded w-16 ml-auto"></div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="card mt-4">
              <h3 className="text-sm font-bold mb-2 text-black">지지/저항선</h3>
              <div className="flex flex-col gap-0.5">
                <div className="sr-level-row sr-resistance-2">
                  <span className="sr-code text-red-500">R2</span>
                  <span className="sr-tag" style={{background:'rgba(239,68,68,0.08)', color:'#dc2626'}}>강한 저항</span>
                  <span className="sr-price text-red-600">${stockDetailTechnical?.technicalData?.R2?.toFixed(2) || '-'}</span>
                </div>
                <div className="sr-level-row sr-resistance-1">
                  <span className="sr-code text-orange-500">R1</span>
                  <span className="sr-tag" style={{background:'rgba(249,115,22,0.08)', color:'#ea580c'}}>저항</span>
                  <span className="sr-price text-orange-600">${stockDetailTechnical?.technicalData?.R1?.toFixed(2) || '-'}</span>
                </div>
                <div className="sr-level-row sr-pivot">
                  <span className="sr-code text-blue-500">PP</span>
                  <span className="sr-tag" style={{background:'rgba(59,130,246,0.08)', color:'#2563eb'}}>피벗</span>
                  <span className="sr-price text-blue-600">${stockDetailTechnical?.technicalData?.Pivot?.toFixed(2) || '-'}</span>
                </div>
                <div className="sr-divider"></div>
                <div className="sr-level-row sr-current">
                  <span className="sr-code" style={{color:'#7c6db5'}}>NOW</span>
                  <span className="sr-tag" style={{background:'rgba(184,167,232,0.15)', color:'#7c6db5'}}>현재가</span>
                  <span className="sr-price" style={{color:'#7c6db5'}}>${stockDetailTechnical?.technicalData?.Close?.toFixed(2) || '-'}</span>
                </div>
                <div className="sr-divider"></div>
                <div className="sr-level-row sr-support-1">
                  <span className="sr-code text-green-500">S1</span>
                  <span className="sr-tag" style={{background:'rgba(34,197,94,0.08)', color:'#16a34a'}}>지지</span>
                  <span className="sr-price text-green-600">${stockDetailTechnical?.technicalData?.S1?.toFixed(2) || '-'}</span>
                </div>
                <div className="sr-level-row sr-support-2">
                  <span className="sr-code text-green-700">S2</span>
                  <span className="sr-tag" style={{background:'rgba(22,163,74,0.08)', color:'#15803d'}}>강한 지지</span>
                  <span className="sr-price text-green-700">${stockDetailTechnical?.technicalData?.S2?.toFixed(2) || '-'}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        

        {/* 펀더멘털 분석 섹션 */}
        
        <div className="feature-section">
          <div className="flex items-center justify-between gap-1 mb-1">
            {/* 좌측: 아이콘 + 제목 + 설명글 */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="feature-icon">
                  <DollarSign size={20} />
                </div>
                <h2 className="text-xm font-bold text-black">펀더멘털 분석</h2>
              </div>
              <p className="text-xs text-gray-600 pl-1">
                기업의 재무 상태와 성장성을 분석하여 장기적인 투자 가치를 평가합니다.
              </p>
            </div>

            {/* 우측: 스코어 */}
            {loading ? (
              <div className="animate-pulse">
                <div className="w-27 h-27 rounded-full bg-gray-200"></div>
              </div>
            ) : (
              <AnimatedCircularProgress
                key={`fundamental-${stockName}-${fundamentalData?.score ?? 0}`}
                score={fundamentalData?.score || 0}
                label={fundamentalData?.recommendedPosition || "오류"}
                size={108}
                delay={300}
              />
            )}
          </div>
          
          {loading ? (
            <div className="ai-summary-card loading animate-pulse mb-4">
              <div className="h-3 bg-gray-100 rounded mb-3 w-20"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-2/3"></div>
            </div>
          ) : (
            <div className="ai-summary-card">
              <div className="ai-summary-header">
                <div className="flex items-center gap-1.5">
                  <span className="ai-badge"><Zap size={9} />AI</span>
                  <span className="ai-summary-title">핵심 요약</span>
                </div>
                <button className="kakao-share-btn" onClick={() => handleKakaoShare('기본적 분석', fundamentalData?.summary)} disabled={!fundamentalData?.summary}>
                  <img src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_medium.png" alt="카카오톡 공유" style={{ width: 14, height: 14 }} />
                  공유
                </button>
              </div>
              <p className="ai-summary-text">{fundamentalData?.summary}</p>
              {fundamentalData?.hashtag && fundamentalData.hashtag.length > 0 && (
                <div className="ai-hashtag-row">
                  {fundamentalData.hashtag.map((kw, i) => (
                    <span key={i} className="ai-hashtag">{kw}</span>
                  ))}
                </div>
              )}
              <button className="ai-full-btn" onClick={() => handleFullAnalysisClick("fundamental")}>
                <span>전체 분석 보기</span>
                <span className="ai-full-btn-arrow"><ChevronRight size={12} /></span>
              </button>
            </div>
          )}
          
          {loading ? (
            <div className="grid grid-cols-2 gap-2 mt-4 animate-pulse">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="tech-indicator-card">
                  <div className="flex items-center justify-between mb-1">
                    <div className="h-2.5 bg-gray-200 rounded w-16"></div>
                    <div className="h-4 bg-gray-200 rounded w-10"></div>
                  </div>
                  <div className="tech-gauge-bar"><div className="tech-gauge-fill bg-gray-200" style={{width:'55%'}}></div></div>
                  <div className="h-6 bg-gray-200 rounded w-12 mt-2"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 mt-4">
              {/* PER */}
              {(() => {
                const val = stockDetailFundamental?.fundamentalData.trailingPER;
                const isHigh = val > 25, isLow = val < 15;
                const color = isLow ? '#10b981' : isHigh ? '#ef4444' : '#B8A7E8';
                const badgeCls = isLow ? 'tech-status-oversold' : isHigh ? 'tech-status-overbought' : 'tech-status-neutral';
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label flex items-center gap-1">PER
                        <button className="relative" onMouseEnter={() => setShowTooltip('pe')} onMouseLeave={() => setShowTooltip(null)}>
                          <Info size={9} className="text-gray-300 hover:text-gray-500" />
                          {showTooltip === 'pe' && (<div className="metric-tooltip" style={{right:'auto',left:0}}><div className="metric-tooltip-title">📊 P/E 비율</div><div className="metric-tooltip-desc">주가를 주당순이익으로 나눈 값. 수익 대비 주가 수준을 나타냅니다.</div><div className="metric-tooltip-item">• 15 이하: 저평가</div><div className="metric-tooltip-item">• 15~25: 적정가치</div><div className="metric-tooltip-item">• 25 이상: 고평가</div></div>)}
                        </button>
                      </span>
                      <span className={`tech-status-badge ${badgeCls}`}>{isLow ? '저평가' : isHigh ? '고평가' : '적정'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{width:`${Math.min((val||0)/50*100,100)}%`,background:color}}></div>
                      <div className="tech-gauge-marker" style={{left:'30%'}}></div>
                      <div className="tech-gauge-marker" style={{left:'50%'}}></div>
                    </div>
                    <div className="flex items-baseline gap-0.5">
                      <span className="tech-indicator-value" style={{color}}>{val?.toFixed(1)||'-'}</span>
                      <span className="text-[10px] text-gray-400 font-medium">x</span>
                    </div>
                  </div>
                );
              })()}
              {/* PBR */}
              {(() => {
                const val = stockDetailFundamental?.fundamentalData.pbr;
                const isHigh = val > 3, isLow = val < 1;
                const color = isLow ? '#10b981' : isHigh ? '#ef4444' : '#B8A7E8';
                const badgeCls = isLow ? 'tech-status-oversold' : isHigh ? 'tech-status-overbought' : 'tech-status-neutral';
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label flex items-center gap-1">PBR
                        <button className="relative" onMouseEnter={() => setShowTooltip('pb')} onMouseLeave={() => setShowTooltip(null)}>
                          <Info size={9} className="text-gray-300 hover:text-gray-500" />
                          {showTooltip === 'pb' && (<div className="metric-tooltip"><div className="metric-tooltip-title">📈 P/B 비율</div><div className="metric-tooltip-desc">주가를 주당순자산으로 나눈 값. 자산가치 대비 주가를 평가합니다.</div><div className="metric-tooltip-item">• 1 이하: 저평가</div><div className="metric-tooltip-item">• 1~3: 적정가치</div><div className="metric-tooltip-item">• 3 이상: 고평가</div></div>)}
                        </button>
                      </span>
                      <span className={`tech-status-badge ${badgeCls}`}>{isLow ? '저평가' : isHigh ? '고평가' : '적정'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{width:`${Math.min((val||0)/6*100,100)}%`,background:color}}></div>
                      <div className="tech-gauge-marker" style={{left:'17%'}}></div>
                      <div className="tech-gauge-marker" style={{left:'50%'}}></div>
                    </div>
                    <div className="flex items-baseline gap-0.5">
                      <span className="tech-indicator-value" style={{color}}>{val?.toFixed(1)||'-'}</span>
                      <span className="text-[10px] text-gray-400 font-medium">x</span>
                    </div>
                  </div>
                );
              })()}
              {/* ROE */}
              {(() => {
                const val = stockDetailFundamental?.fundamentalData.roe;
                const isGood = val > 15, isPoor = val < 10;
                const color = isGood ? '#10b981' : isPoor ? '#ef4444' : '#B8A7E8';
                const badgeCls = isGood ? 'tech-status-oversold' : isPoor ? 'tech-status-overbought' : 'tech-status-neutral';
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label flex items-center gap-1">ROE
                        <button className="relative" onMouseEnter={() => setShowTooltip('roe')} onMouseLeave={() => setShowTooltip(null)}>
                          <Info size={9} className="text-gray-300 hover:text-gray-500" />
                          {showTooltip === 'roe' && (<div className="metric-tooltip" style={{right:'auto',left:0}}><div className="metric-tooltip-title">📊 ROE (자기자본이익률)</div><div className="metric-tooltip-desc">주주 자본을 얼마나 효율적으로 활용해 수익을 창출하는지 나타냅니다.</div><div className="metric-tooltip-item">• 15% 이상: 우수</div><div className="metric-tooltip-item">• 10~15%: 보통</div><div className="metric-tooltip-item">• 10% 이하: 부진</div></div>)}
                        </button>
                      </span>
                      <span className={`tech-status-badge ${badgeCls}`}>{isGood ? '우수' : isPoor ? '부진' : '보통'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{width:`${Math.min((val||0)/40*100,100)}%`,background:color}}></div>
                      <div className="tech-gauge-marker" style={{left:'25%'}}></div>
                      <div className="tech-gauge-marker" style={{left:'37.5%'}}></div>
                    </div>
                    <div className="flex items-baseline gap-0.5">
                      <span className="tech-indicator-value" style={{color}}>{val?.toFixed(1)||'-'}</span>
                      <span className="text-[10px] text-gray-400 font-medium">%</span>
                    </div>
                  </div>
                );
              })()}
              {/* 영업 마진 */}
              {(() => {
                const val = stockDetailFundamental?.fundamentalData.operatingMargin;
                const isGood = val > 20, isPoor = val < 10;
                const color = isGood ? '#10b981' : isPoor ? '#ef4444' : '#B8A7E8';
                const badgeCls = isGood ? 'tech-status-oversold' : isPoor ? 'tech-status-overbought' : 'tech-status-neutral';
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label flex items-center gap-1">영업마진
                        <button className="relative" onMouseEnter={() => setShowTooltip('margin')} onMouseLeave={() => setShowTooltip(null)}>
                          <Info size={9} className="text-gray-300 hover:text-gray-500" />
                          {showTooltip === 'margin' && (<div className="metric-tooltip"><div className="metric-tooltip-title">💰 영업 마진</div><div className="metric-tooltip-desc">매출액에서 영업이익이 차지하는 비율. 본업 수익성을 보여줍니다.</div><div className="metric-tooltip-item">• 20% 이상: 우수</div><div className="metric-tooltip-item">• 10~20%: 보통</div><div className="metric-tooltip-item">• 10% 이하: 부진</div></div>)}
                        </button>
                      </span>
                      <span className={`tech-status-badge ${badgeCls}`}>{isGood ? '우수' : isPoor ? '부진' : '보통'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{width:`${Math.min((val||0)/50*100,100)}%`,background:color}}></div>
                      <div className="tech-gauge-marker" style={{left:'20%'}}></div>
                      <div className="tech-gauge-marker" style={{left:'40%'}}></div>
                    </div>
                    <div className="flex items-baseline gap-0.5">
                      <span className="tech-indicator-value" style={{color}}>{val?.toFixed(1)||'-'}</span>
                      <span className="text-[10px] text-gray-400 font-medium">%</span>
                    </div>
                  </div>
                );
              })()}
              {/* 매출 성장률 */}
              {(() => {
                const val = stockDetailFundamental?.fundamentalData.revenueGrowth5Y;
                const isGood = val > 10, isPoor = val < 0;
                const color = isGood ? '#10b981' : isPoor ? '#ef4444' : '#B8A7E8';
                const badgeCls = isGood ? 'tech-status-oversold' : isPoor ? 'tech-status-overbought' : 'tech-status-neutral';
                const pct = Math.min(Math.max(((val||0)+30)/80*100, 0), 100);
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label flex items-center gap-1">매출성장률
                        <button className="relative" onMouseEnter={() => setShowTooltip('growth')} onMouseLeave={() => setShowTooltip(null)}>
                          <Info size={9} className="text-gray-300 hover:text-gray-500" />
                          {showTooltip === 'growth' && (<div className="metric-tooltip" style={{right:'auto',left:0}}><div className="metric-tooltip-title">📈 매출 성장률 (5Y)</div><div className="metric-tooltip-desc">최근 5년간 연평균 매출 증가율. 사업 확장 능력을 나타냅니다.</div><div className="metric-tooltip-item">• 10% 이상: 고성장</div><div className="metric-tooltip-item">• 0~10%: 안정 성장</div><div className="metric-tooltip-item">• 0% 이하: 성장 정체</div></div>)}
                        </button>
                      </span>
                      <span className={`tech-status-badge ${badgeCls}`}>{isGood ? '고성장' : isPoor ? '정체' : '안정'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{width:`${pct}%`,background:color}}></div>
                      <div className="tech-gauge-marker" style={{left:'37.5%'}}></div>
                      <div className="tech-gauge-marker" style={{left:'50%'}}></div>
                    </div>
                    <div className="flex items-baseline gap-0.5">
                      <span className="tech-indicator-value" style={{color}}>{val != null ? `${val>0?'+':''}${val.toFixed(1)}` : '-'}</span>
                      <span className="text-[10px] text-gray-400 font-medium">%</span>
                    </div>
                  </div>
                );
              })()}
              {/* EPS 성장률 */}
              {(() => {
                const val = stockDetailFundamental?.fundamentalData.epsGrowth5Y;
                const isGood = val > 15, isPoor = val < 0;
                const color = isGood ? '#10b981' : isPoor ? '#ef4444' : '#B8A7E8';
                const badgeCls = isGood ? 'tech-status-oversold' : isPoor ? 'tech-status-overbought' : 'tech-status-neutral';
                const pct = Math.min(Math.max(((val||0)+30)/80*100, 0), 100);
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label flex items-center gap-1">EPS성장률
                        <button className="relative" onMouseEnter={() => setShowTooltip('eps')} onMouseLeave={() => setShowTooltip(null)}>
                          <Info size={9} className="text-gray-300 hover:text-gray-500" />
                          {showTooltip === 'eps' && (<div className="metric-tooltip"><div className="metric-tooltip-title">💎 EPS 성장률 (5Y)</div><div className="metric-tooltip-desc">주당순이익의 5년 평균 성장률. 수익 창출 능력 개선 정도를 보여줍니다.</div><div className="metric-tooltip-item">• 15% 이상: 우수</div><div className="metric-tooltip-item">• 0~15%: 안정</div><div className="metric-tooltip-item">• 0% 이하: 부진</div></div>)}
                        </button>
                      </span>
                      <span className={`tech-status-badge ${badgeCls}`}>{isGood ? '우수' : isPoor ? '부진' : '안정'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{width:`${pct}%`,background:color}}></div>
                      <div className="tech-gauge-marker" style={{left:'37.5%'}}></div>
                      <div className="tech-gauge-marker" style={{left:'56%'}}></div>
                    </div>
                    <div className="flex items-baseline gap-0.5">
                      <span className="tech-indicator-value" style={{color}}>{val != null ? `${val>0?'+':''}${val.toFixed(1)}` : '-'}</span>
                      <span className="text-[10px] text-gray-400 font-medium">%</span>
                    </div>
                  </div>
                );
              })()}
              {/* Forward PER */}
              {(() => {
                const val = stockDetailFundamental?.fundamentalData.forwardPER;
                const isHigh = val > 25, isLow = val < 15;
                const color = isLow ? '#10b981' : isHigh ? '#ef4444' : '#B8A7E8';
                const badgeCls = isLow ? 'tech-status-oversold' : isHigh ? 'tech-status-overbought' : 'tech-status-neutral';
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label flex items-center gap-1">Fwd PER
                        <button className="relative" onMouseEnter={() => setShowTooltip('forwardPE')} onMouseLeave={() => setShowTooltip(null)}>
                          <Info size={9} className="text-gray-300 hover:text-gray-500" />
                          {showTooltip === 'forwardPE' && (<div className="metric-tooltip" style={{right:'auto',left:0}}><div className="metric-tooltip-title">🔮 Forward P/E</div><div className="metric-tooltip-desc">향후 12개월 예상 EPS 기준 PER. 미래 수익성 대비 현재 주가 밸류에이션을 평가합니다.</div><div className="metric-tooltip-item">• 15 이하: 저평가</div><div className="metric-tooltip-item">• 15~25: 적정가치</div><div className="metric-tooltip-item">• 25 이상: 고평가</div></div>)}
                        </button>
                      </span>
                      <span className={`tech-status-badge ${badgeCls}`}>{isLow ? '저평가' : isHigh ? '고평가' : '적정'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{width:`${Math.min((val||0)/50*100,100)}%`,background:color}}></div>
                      <div className="tech-gauge-marker" style={{left:'30%'}}></div>
                      <div className="tech-gauge-marker" style={{left:'50%'}}></div>
                    </div>
                    <div className="flex items-baseline gap-0.5">
                      <span className="tech-indicator-value" style={{color}}>{stockDetailFundamental?.fundamentalData.forwardPER?.toFixed(1)||'-'}</span>
                      <span className="text-[10px] text-gray-400 font-medium">x</span>
                    </div>
                  </div>
                );
              })()}
              {/* Trailing PER */}
              {(() => {
                const val = stockDetailFundamental?.fundamentalData.trailingPER;
                const isHigh = val > 25, isLow = val < 15;
                const color = isLow ? '#10b981' : isHigh ? '#ef4444' : '#B8A7E8';
                const badgeCls = isLow ? 'tech-status-oversold' : isHigh ? 'tech-status-overbought' : 'tech-status-neutral';
                return (
                  <div className="tech-indicator-card">
                    <div className="flex items-center justify-between">
                      <span className="tech-indicator-label flex items-center gap-1">TTM PER
                        <button className="relative" onMouseEnter={() => setShowTooltip('trailingPE')} onMouseLeave={() => setShowTooltip(null)}>
                          <Info size={9} className="text-gray-300 hover:text-gray-500" />
                          {showTooltip === 'trailingPE' && (<div className="metric-tooltip"><div className="metric-tooltip-title">📊 Trailing P/E</div><div className="metric-tooltip-desc">과거 12개월 실제 EPS 기준 PER. 현재까지의 실적 대비 주가 밸류에이션을 나타냅니다.</div><div className="metric-tooltip-item">• 15 이하: 저평가</div><div className="metric-tooltip-item">• 15~25: 적정가치</div><div className="metric-tooltip-item">• 25 이상: 고평가</div></div>)}
                        </button>
                      </span>
                      <span className={`tech-status-badge ${badgeCls}`}>{isLow ? '저평가' : isHigh ? '고평가' : '적정'}</span>
                    </div>
                    <div className="tech-gauge-bar">
                      <div className="tech-gauge-fill" style={{width:`${Math.min((val||0)/50*100,100)}%`,background:color}}></div>
                      <div className="tech-gauge-marker" style={{left:'30%'}}></div>
                      <div className="tech-gauge-marker" style={{left:'50%'}}></div>
                    </div>
                    <div className="flex items-baseline gap-0.5">
                      <span className="tech-indicator-value" style={{color}}>{stockDetailFundamental?.fundamentalData.trailingPER?.toFixed(1)||'-'}</span>
                      <span className="text-[10px] text-gray-400 font-medium">x</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* 애널리스트 의견 섹션 */}
          {loading ? (
            <div className="analyst-opinion-card animate-pulse">
              <div className="analyst-opinion-header">
                <div className="h-3 bg-gray-100 rounded w-28"></div>
                <div className="h-3 bg-gray-100 rounded w-14"></div>
              </div>
              <div className="analyst-panel-grid">
                <div className="analyst-panel">
                  <div className="h-2 bg-gray-100 rounded mb-3 w-16"></div>
                  <div className="h-6 bg-gray-100 rounded mb-2 w-20"></div>
                  <div className="h-3 bg-gray-100 rounded w-12"></div>
                </div>
                <div className="analyst-panel">
                  <div className="h-2 bg-gray-100 rounded mb-3 w-16"></div>
                  <div className="h-6 bg-gray-100 rounded mb-3 w-20"></div>
                  <div className="h-2 bg-gray-100 rounded w-full"></div>
                </div>
              </div>
            </div>
          ) : stockDetailFundamental?.fundamentalData.numberOfAnalystOpinions ? (
            (() => {
              const fd = stockDetailFundamental.fundamentalData;
              const rk = fd.recommendationKey;
              const verdictMap = {
                strong_buy: ['강력 매수', 'strong-buy'],
                buy: ['매수', 'buy'],
                hold: ['보유', 'hold'],
                sell: ['매도', 'sell'],
                strong_sell: ['강력 매도', 'strong-sell'],
              };
              const [verdictText, verdictClass] = verdictMap[rk] || [rk || '-', 'hold'];
              const low = fd.targetLowPrice, high = fd.targetHighPrice, mean = fd.targetMeanPrice;
              const pct = low && high && mean
                ? Math.max(4, Math.min(96, ((mean - low) / (high - low)) * 100))
                : 50;
              return (
                <div className="analyst-opinion-card">
                  <div className="analyst-opinion-header">
                    <div className="analyst-opinion-title">
                      <Users size={14} className="text-blue-500" />
                      애널리스트 의견
                    </div>
                    <span className="analyst-count-badge">{fd.numberOfAnalystOpinions}명 참여</span>
                  </div>
                  <div className="analyst-panel-grid">
                    {/* 추천 등급 */}
                    <div className="analyst-panel">
                      <div className="analyst-panel-label">추천 등급</div>
                      <div className={`analyst-verdict ${verdictClass}`}>{verdictText}</div>
                      <span className="analyst-score-chip">
                        점수 {fd.recommendationMean || '-'} <span style={{opacity:0.5}}>/ 5.0</span>
                      </span>
                    </div>
                    {/* 평균 목표가 */}
                    <div className="analyst-panel">
                      <div className="analyst-panel-label">평균 목표가</div>
                      <div className="analyst-target-price">${mean?.toFixed(2) || '-'}</div>
                      <div className="analyst-range-track">
                        <div className="analyst-range-dot" style={{ left: `${pct}%` }} />
                      </div>
                      <div className="analyst-range-labels">
                        <span className="analyst-range-low">${low?.toFixed(0) || '-'}</span>
                        <span className="analyst-range-high">${high?.toFixed(0) || '-'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : null}

          {/* {loading ? (
            <div className="card mt-4 animate-pulse">
              <h3 className="text-sm font-bold mb-3 text-black">배당금 정보</h3>
              <div className="flex">
                <div className="flex-1 pr-4 border-r">
                  <div className="h-6 bg-gray-200 rounded mb-1"></div>
                  <div className="text-xs text-gray-500">분기당 주당 배당금</div>
                  <div className="flex justify-between mt-3">
                    <div>
                      <div className="text-xs text-gray-500">배당 수익률</div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">배당성향</div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
                <div className="flex-1 pl-4">
                  <h4 className="text-xs font-semibold mb-2">최근 배당금 지급</h4>
                  <div className="text-xs">
                    <div className="flex justify-between py-1 border-b">
                      <div className="h-3 bg-gray-200 rounded w-20"></div>
                      <div className="h-3 bg-gray-200 rounded w-10"></div>
                    </div>
                    <div className="flex justify-between py-1 border-b">
                      <div className="h-3 bg-gray-200 rounded w-20"></div>
                      <div className="h-3 bg-gray-200 rounded w-10"></div>
                    </div>
                    <div className="flex justify-between py-1">
                      <div className="h-3 bg-gray-200 rounded w-20"></div>
                      <div className="h-3 bg-gray-200 rounded w-10"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card mt-4">
              <h3 className="text-sm font-bold mb-3 text-black">배당금 정보</h3>
              <div className="flex">
                <div className="flex-1 pr-4 border-r">
                  <div className="text-lg font-bold text-green-500 mb-1">{stockDetailFundamental?.fundamentalData.dividendYield ? `${stockDetailFundamental.fundamentalData.dividendYield}%` : '-'}</div>
                  <div className="text-xs text-gray-500">배당 수익률</div>
                  <div className="flex justify-between mt-3">
                    <div>
                      <div className="text-xs text-gray-500">5년 평균 배당률</div>
                      <div className="text-sm font-semibold">{stockDetailFundamental?.fundamentalData.fiveYearAvgDividendYield ? `${stockDetailFundamental.fundamentalData.fiveYearAvgDividendYield}%` : '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">배당성향</div>
                      <div className="text-sm font-semibold">{stockDetailFundamental?.fundamentalData.payoutRatio ? `${stockDetailFundamental.fundamentalData.payoutRatio}%` : '-'}</div>
                    </div>
                  </div>
                </div>
                <div className="flex-1 pl-4">
                  <h4 className="text-xs font-semibold mb-2">추가 재무 지표</h4>
                  <div className="text-xs">
                    <div className="flex justify-between py-1 border-b">
                      <span>Current Ratio</span>
                      <span className="text-right">{stockDetailFundamental?.fundamentalData.currentRatio?.toFixed(1) || '-'}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b">
                      <span>Quick Ratio</span>
                      <span className="text-right">{stockDetailFundamental?.fundamentalData.quickRatio?.toFixed(1) || '-'}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b">
                      <span>Debt/Equity</span>
                      <span className="text-right">{stockDetailFundamental?.fundamentalData.debtToEquity?.toFixed(1) || '-'}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b">
                      <span>ROA</span>
                      <span className="text-right">{stockDetailFundamental?.fundamentalData.roa ? `${stockDetailFundamental.fundamentalData.roa.toFixed(1)}%` : '-'}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span>EV/EBITDA</span>
                      <span className="text-right">{stockDetailFundamental?.fundamentalData.evToEbitda?.toFixed(1) || '-'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )} */}
        </div>
        


        {/* 뉴스 섹션 */}
        
        <div className="feature-section">
          <div className="flex items-center justify-between gap-1 mb-1">
            {/* 좌측: 아이콘 + 제목 + 설명글 */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="feature-icon">
                  <Newspaper size={16} />
                </div>
                <h2 className="text-xm font-bold text-black">뉴스</h2>
              </div>
              <p className="text-xs text-gray-600 pl-1">
                최신 기업 뉴스와 시장 동향 정보를 확인하여 투자 결정에 활용하세요.
              </p>
            </div>

            {/* 우측: 스코어 */}
            {loading ? (
              <div className="animate-pulse">
                <div className="w-27 h-27 rounded-full bg-gray-200"></div>
              </div>
            ) : (
              <AnimatedCircularProgress
                key={`news-${stockName}-${newsData?.score ?? 0}`}
                score={newsData?.score || 0}
                label={newsData?.recommendedPosition || "오류"}
                size={108}
                delay={300}
              />
            )}
          </div>
          
          {loading ? (
            <div className="ai-summary-card loading animate-pulse mb-4">
              <div className="h-3 bg-gray-100 rounded mb-3 w-20"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-2/3"></div>
            </div>
          ) : (
            <div className="ai-summary-card">
              <div className="ai-summary-header">
                <div className="flex items-center gap-1.5">
                  <span className="ai-badge"><Zap size={9} />AI</span>
                  <span className="ai-summary-title">핵심 요약</span>
                </div>
                <button className="kakao-share-btn" onClick={() => handleKakaoShare('뉴스 분석', newsData?.summary)} disabled={!newsData?.summary && !stockDetailNews?.newsData}>
                  <img src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_medium.png" alt="카카오톡 공유" style={{ width: 14, height: 14 }} />
                  공유
                </button>
              </div>
              <p className="ai-summary-text">
                {stockDetailNews?.newsData ?
                  `뉴스 분석 결과 ${stockName}은 ${stockDetailNews.newsData.overall_sentiment}입니다. 총 ${stockDetailNews.newsData.news_count}건의 뉴스 중 긍정 ${stockDetailNews.newsData.positive_count}건, 부정 ${stockDetailNews.newsData.negative_count}건, 중립 ${stockDetailNews.newsData.neutral_count}건으로 평균 감성 점수는 ${(stockDetailNews.newsData.avg_sentiment * 100).toFixed(1)}점입니다.`
                  : newsData?.summary || `뉴스 분석 결과 ${stockName}은 강력한 매수 신호를 보이고 있습니다.`
                }
              </p>
              {newsData?.hashtag && newsData.hashtag.length > 0 && (
                <div className="ai-hashtag-row">
                  {newsData.hashtag.map((kw, i) => (
                    <span key={i} className="ai-hashtag">{kw}</span>
                  ))}
                </div>
              )}
              <button className="ai-full-btn" onClick={() => handleFullAnalysisClick("news")}>
                <span>전체 분석 보기</span>
                <span className="ai-full-btn-arrow"><ChevronRight size={12} /></span>
              </button>
            </div>
          )}
          
          {loading ? (
            <>
              <div className="card animate-pulse">
                <div className="h-3 bg-gray-200 rounded mb-1 w-32"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded mb-2 w-3/4"></div>
                <div className="flex justify-between">
                  <div className="flex space-x-2">
                    <div className="h-5 bg-gray-200 rounded px-3"></div>
                    <div className="h-5 bg-gray-200 rounded px-3"></div>
                  </div>
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
              <div className="card mt-4 animate-pulse">
                <div className="h-3 bg-gray-200 rounded mb-1 w-32"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded mb-2 w-3/4"></div>
                <div className="flex justify-between">
                  <div className="flex space-x-2">
                    <div className="h-5 bg-gray-200 rounded px-3"></div>
                    <div className="h-5 bg-gray-200 rounded px-3"></div>
                  </div>
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
            </>
          ) : (
            <>
              {getNewsPaginatedPosts().length > 0 ? (
                <>
                  {getNewsPaginatedPosts().map((article, index) => (
                    <div key={article.id || index} className={`card ${index > 0 ? 'mt-4' : ''}`}>
                      <div className="text-xs text-blue-600 font-semibold mb-1">
                        {article.source} • {new Date(article.publish_date).toLocaleDateString('ko-KR', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      <h3 className="text-sm font-bold mb-1">{article.title}</h3>
                      <p className="text-xs text-gray-600 mb-2">
                        {article.summary}
                      </p>
                      <div className="flex justify-between items-center">
                        <div className="flex gap-1">
                          <span className={`text-xs px-2 py-1 rounded ${
                            article.sentiment_score > 0.5 ? 'bg-green-100 text-green-600' :
                            article.sentiment_score < -0.5 ? 'bg-red-100 text-red-600' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {article.sentiment_score > 0.5 ? '긍정' :
                             article.sentiment_score < -0.5 ? '부정' : '중립'}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            article.impact === '강한 호재' ? 'bg-blue-100 text-blue-600' :
                            article.impact === '호재' ? 'bg-green-100 text-green-600' :
                            article.impact === '악재' ? 'bg-red-100 text-red-600' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {article.impact || '보통'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* 번역 버튼 */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleTranslate(`${article.title}\n\n${article.summary}`);
                            }}
                            className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1 cursor-pointer bg-transparent border-none p-0 transition-colors"
                            title="구글 번역으로 열기"
                          >
                            <Languages size={16} />
                          </button>
                          {/* 원문 링크 버튼 */}
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleExternalLink(article.url);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center cursor-pointer bg-transparent border-none p-0 transition-colors"
                            title="원문 보기"
                          >
                            <CircleChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* 뉴스 페이지네이션 */}
                  {getNewsTotalPages() > 1 && (
                    <div className="flex items-center justify-between mt-6">
                      
                      <div className="text-xs text-gray-500">  </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleNewsPageChange(newsCurrentPage - 1)}
                          disabled={newsCurrentPage === 1}
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-300 transition-all"
                        >
                          <ChevronLeft size={14} />
                        </button>
                        
                        <div className="flex space-x-1">
                          {generatePageNumbers(newsCurrentPage, getNewsTotalPages()).map((page, index) => (
                            page === '...' ? (
                              <span key={`dots-${index}`} className="px-2 py-1 text-xs text-gray-400">
                                ...
                              </span>
                            ) : (
                              <button
                                key={page}
                                onClick={() => handleNewsPageChange(page)}
                                className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold transition-all ${
                                  newsCurrentPage === page
                                    ? 'bg-[#B8A7E8] text-white shadow-[0_2px_8px_rgba(184,167,232,0.35)]'
                                    : 'text-gray-400 hover:bg-gray-50 hover:text-gray-700'
                                }`}
                              >
                                {page}
                              </button>
                            )
                          ))}
                        </div>
                        
                        <button
                          onClick={() => handleNewsPageChange(newsCurrentPage + 1)}
                          disabled={newsCurrentPage === getNewsTotalPages()}
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-300 transition-all"
                        >
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // Fallback if no news data
                <div className="card mt-4">
                  <div className="text-center py-4">
                    <p className="text-gray-500">뉴스 데이터를 불러올 수 없습니다.</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        

        {/* 커뮤니티 섹션 */}
        
        {/* {stockName !== "SIRI" ? ( */}
        <div className="feature-section">
          <div className="flex items-center justify-between gap-1 mb-1">
            {/* 좌측: 아이콘 + 제목 + 설명글 */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="feature-icon">
                  <MessageSquare size={16} />
                </div>
                <h2 className="text-xm font-bold text-black">커뮤니티</h2>
              </div>
              <p className="text-xs text-gray-600 pl-1">
                투자자들의 실시간 의견을 확인하고 다양한 투자 인사이트를 얻으세요.
              </p>
            </div>

            {/* 우측: 스코어 */}
            {loading ? (
              <div className="animate-pulse">
                <div className="w-27 h-27 rounded-full bg-gray-200"></div>
              </div>
            ) : (
              <AnimatedCircularProgress
                key={`sentiment-${stockName}-${sentimentData?.score ?? 0}`}
                score={sentimentData?.score || 0}
                label={sentimentData?.recommendedPosition || "오류"}
                size={108}
                delay={300}
              />
            )}
          </div>
          
          {loading ? (
            <div className="ai-summary-card loading animate-pulse mb-4">
              <div className="h-3 bg-gray-100 rounded mb-3 w-20"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-2/3"></div>
            </div>
          ) : (
            <div className="ai-summary-card">
              <div className="ai-summary-header">
                <div className="flex items-center gap-1.5">
                  <span className="ai-badge"><Zap size={9} />AI</span>
                  <span className="ai-summary-title">핵심 요약</span>
                </div>
                <button className="kakao-share-btn" onClick={() => handleKakaoShare('감성 분석', sentimentData?.summary)} disabled={!sentimentData?.summary}>
                  <img src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_medium.png" alt="카카오톡 공유" style={{ width: 14, height: 14 }} />
                  공유
                </button>
              </div>
              <p className="ai-summary-text">{sentimentData?.summary}</p>
              {sentimentData?.hashtag && sentimentData.hashtag.length > 0 && (
                <div className="ai-hashtag-row">
                  {sentimentData.hashtag.map((kw, i) => (
                    <span key={i} className="ai-hashtag">{kw}</span>
                  ))}
                </div>
              )}
              <button className="ai-full-btn" onClick={() => handleFullAnalysisClick("sentiment")}>
                <span>전체 분석 보기</span>
                <span className="ai-full-btn-arrow"><ChevronRight size={12} /></span>
              </button>
            </div>
          )}

          {loading ? (
            <>
              <div className="card mt-4 animate-pulse">
                <div className="flex items-start">
                  <div className="w-9 h-9 rounded-full bg-gray-200 mr-3 flex-shrink-0"></div>
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                      <div className="h-3 bg-gray-200 rounded w-12 ml-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-10 ml-auto"></div>
                    </div>
                    <div className="h-3 bg-gray-200 rounded mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded mb-1 w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-16 mt-2"></div>
                  </div>
                </div>
              </div>
              <div className="card mt-4 animate-pulse">
                <div className="flex items-start">
                  <div className="w-9 h-9 rounded-full bg-gray-200 mr-3 flex-shrink-0"></div>
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                      <div className="h-3 bg-gray-200 rounded w-12 ml-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-10 ml-auto"></div>
                    </div>
                    <div className="h-3 bg-gray-200 rounded mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded mb-1 w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-16 mt-2"></div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {getCommunityPaginatedPosts().length > 0 ? (
                <>
                  {getCommunityPaginatedPosts().map((post, index) => (
                    <div key={post.post_id} className="card mt-4">
                      <div className="flex items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center">
                            <div className="flex items-center">
                              <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="#FF4500">
                                <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                              </svg>
                              {/* <span className="font-bold text-sm">r/{post.subreddit}</span> */}
                            </div>
                            <span className="text-xs text-gray-500 ml-2">
                              {new Date(post.post_created_at).toLocaleDateString('ko-KR', { 
                                month: 'short', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            <span className={`ml-auto text-xs px-2 py-1 rounded ${
                              parseInt(post.post_score) > 1000 ? 'bg-green-100 text-green-600' :
                              parseInt(post.post_score) > 100 ? 'bg-blue-100 text-blue-600' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              추천수: {post.post_score}
                            </span>
                          </div>
                          <h4 className="text-sm font-semibold mt-1 mb-1 break-words overflow-hidden">{post.post_title}</h4>
                          {post.post_content && (
                            <p className="text-xs text-gray-700 mt-1 mb-2 break-words overflow-hidden">
                              {post.post_content.substring(0, 100)}{post.post_content.length > 100 ? '...' : ''}
                            </p>
                          )}
                          <div className="flex mt-2 justify-between items-center">
                            <div className="bg-gray-100 rounded-full px-2 py-1 text-xs flex items-center text-gray-600">
                              <MessageSquare size={12} className="mr-1" /> {post.post_num_comments}
                            </div>
                            <div className="flex items-center gap-2">
                              {/* 번역 버튼 */}
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  const textToTranslate = `${post.post_title}${post.post_content ? '\n\n' + post.post_content : ''}`;
                                  handleTranslate(textToTranslate);
                                }}
                                className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1 cursor-pointer bg-transparent border-none p-0 transition-colors"
                                title="구글 번역으로 열기"
                              >
                                <Languages size={16} />
                              </button>
                              {/* 원문 링크 버튼 */}
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleExternalLink(`https://reddit.com${post.post_permalink}`);
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center cursor-pointer bg-transparent border-none p-0 transition-colors"
                                title="원문 보기"
                              >
                                <CircleChevronRight size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* 커뮤니티 페이지네이션 */}
                  {getCommunityTotalPages() > 1 && (
                    <div className="flex items-center justify-between mt-6">
                      <div className="text-xs text-gray-500">  </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleCommunityPageChange(communityCurrentPage - 1)}
                          disabled={communityCurrentPage === 1}
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-300 transition-all"
                        >
                          <ChevronLeft size={14} />
                        </button>
                        
                        <div className="flex space-x-1">
                          {generatePageNumbers(communityCurrentPage, getCommunityTotalPages()).map((page, index) => (
                            page === '...' ? (
                              <span key={`dots-${index}`} className="px-2 py-1 text-xs text-gray-400">
                                ...
                              </span>
                            ) : (
                              <button
                                key={page}
                                onClick={() => handleCommunityPageChange(page)}
                                className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold transition-all ${
                                  communityCurrentPage === page
                                    ? 'bg-[#B8A7E8] text-white shadow-[0_2px_8px_rgba(184,167,232,0.35)]'
                                    : 'text-gray-400 hover:bg-gray-50 hover:text-gray-700'
                                }`}
                              >
                                {page}
                              </button>
                            )
                          ))}
                        </div>
                        
                        <button
                          onClick={() => handleCommunityPageChange(communityCurrentPage + 1)}
                          disabled={communityCurrentPage === getCommunityTotalPages()}
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-300 transition-all"
                        >
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                // Fallback if no community data
                <div className="card mt-4">
                  <div className="text-center py-4">
                    <p className="text-gray-500">커뮤니티 데이터를 불러올 수 없습니다.</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        

        {/* 리스크 분석 섹션 */}
        
        <div className="feature-section">
          <div className="flex items-center justify-between gap-1 mb-1">
            {/* 좌측: 아이콘 + 제목 + 설명글 */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="feature-icon">
                  <BarChart2 size={16} />
                </div>
                <h2 className="text-xm font-bold text-black">리스크 분석</h2>
              </div>
              <p className="text-xs text-gray-600 pl-1">
                현재 주식의 리스크를 확인하세요. 낮은 숏 포지션은 긍정적인 시장 심리를 나타냅니다.
              </p>
            </div>

            {/* 우측: 스코어 */}
            {loading ? (
              <div className="animate-pulse">
                <div className="w-27 h-27 rounded-full bg-gray-200"></div>
              </div>
            ) : (
              <AnimatedCircularProgress
                key={`risk-${stockName}-${riskData?.score ?? 0}`}
                score={riskData?.score || 0}
                label={riskData?.recommendedPosition || "오류"}
                size={108}
                delay={300}
              />
            )}
          </div>
          
          {/* <div className="card">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-bold text-black">애널리스트 추천</h3>
              <div className="flex items-center">
                <span className="text-lg font-bold mr-2">4.2</span>
                <span className="text-sm text-green-500 font-semibold">매수</span>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="text-center">
                <div className="w-full bg-gray-200 rounded-full overflow-hidden mb-1">
                  <div className="bg-green-500 h-2" style={{width: '70%'}}></div>
                </div>
                <div className="text-xs">매수 (28)</div>
              </div>
              <div className="text-center">
                <div className="w-full bg-gray-200 rounded-full overflow-hidden mb-1">
                  <div className="bg-yellow-500 h-2" style={{width: '22.5%'}}></div>
                </div>
                <div className="text-xs">보유 (9)</div>
              </div>
              <div className="text-center">
                <div className="w-full bg-gray-200 rounded-full overflow-hidden mb-1">
                  <div className="bg-red-500 h-2" style={{width: '7.5%'}}></div>
                </div>
                <div className="text-xs">매도 (3)</div>
              </div>
            </div>
            
            <h4 className="text-xs font-semibold mb-2">목표가 평균</h4>
            <div className="flex items-center mb-2">
              <span className="text-sm font-bold">$205.50</span>
              <span className="text-xs text-green-500 ml-2">+16.87%</span>
            </div>
            
            <div className="w-full h-2 bg-gray-200 rounded-full mb-1 relative">
              <div className="absolute w-1 h-4 bg-black" style={{left: '45%', top: '-5px'}}></div>
              <div className="absolute w-2 h-2 rounded-full bg-red-500" style={{left: '25%', top: '0px'}}></div>
              <div className="absolute w-2 h-2 rounded-full bg-green-500" style={{left: '85%', top: '0px'}}></div>
            </div>
            <div className="flex justify-between text-xs text-gray-600">
              <span>$160.00</span>
              <span>현재: $175.84</span>
              <span>$230.00</span>
            </div>
          </div> */}
          
          {loading ? (
            <div className="ai-summary-card loading animate-pulse mb-4">
              <div className="h-3 bg-gray-100 rounded mb-3 w-20"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-2/3"></div>
            </div>
          ) : (
            <div className="ai-summary-card">
              <div className="ai-summary-header">
                <div className="flex items-center gap-1.5">
                  <span className="ai-badge"><Zap size={9} />AI</span>
                  <span className="ai-summary-title">핵심 요약</span>
                </div>
                <button className="kakao-share-btn" onClick={() => handleKakaoShare('위험 분석', riskData?.summary)} disabled={!riskData?.summary}>
                  <img src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_medium.png" alt="카카오톡 공유" style={{ width: 14, height: 14 }} />
                  공유
                </button>
              </div>
              <p className="ai-summary-text">{riskData?.summary}</p>
              {riskData?.hashtag && riskData.hashtag.length > 0 && (
                <div className="ai-hashtag-row">
                  {riskData.hashtag.map((kw, i) => (
                    <span key={i} className="ai-hashtag">{kw}</span>
                  ))}
                </div>
              )}
              <button className="ai-full-btn" onClick={() => handleFullAnalysisClick("risk")}>
                <span>전체 분석 보기</span>
                <span className="ai-full-btn-arrow"><ChevronRight size={12} /></span>
              </button>
            </div>
          )}

          {loading ? (
            <div className="card mt-4 animate-pulse">
              <div className="flex justify-between items-center">
                <div className="h-3 bg-gray-200 rounded w-32"></div>
                <div className="h-4 bg-gray-200 rounded w-12"></div>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full my-2"></div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <div className="h-3 bg-gray-200 rounded mb-1"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
                <div>
                  <div className="h-3 bg-gray-200 rounded mb-1"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card mt-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-semibold">공매도 현황</h4>
                <span className="text-xs text-gray-500">
                  {shortPositionData?.asOfDate ? new Date(shortPositionData.asOfDate).toLocaleDateString('ko-KR') : '-'}
                </span>
              </div>
              
              {/* 간단한 요약 설명 */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="relative">
                  <div className="flex items-center mb-1">
                    <div className="text-xs text-gray-500">공매도 비율</div>
                    <button
                      className="ml-1 relative"
                      onMouseEnter={() => setShowTooltip('shortRatio')}
                      onMouseLeave={() => setShowTooltip(null)}
                    >
                      <Info size={12} className="text-gray-400 hover:text-gray-600" />
                      {showTooltip === 'shortRatio' && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                          <div className="mb-1 font-semibold">📊 공매도 비율 기준</div>
                          <div>• 5% 미만: 안전 (낮음)</div>
                          <div>• 5~15%: 보통 (주의)</div>
                          <div>• 15% 이상: 위험 (높음)</div>
                        </div>
                      )}
                    </button>
                  </div>
                  <div className={`text-lg font-bold ${
                    (shortPositionData?.shortPercentFloat || 0) < 0.05 ? 'text-green-500' :
                    (shortPositionData?.shortPercentFloat || 0) < 0.15 ? 'text-yellow-500' : 'text-red-500'
                  }`}>
                    {shortPositionData?.shortPercentFloat ? 
                      `${(shortPositionData.shortPercentFloat * 100).toFixed(1)}%` : '-'}
                  </div>
                  <div className="text-xs text-gray-400">
                    {(shortPositionData?.shortPercentFloat || 0) < 0.05 ? '매우 낮음 😊' :
                     (shortPositionData?.shortPercentFloat || 0) < 0.15 ? '보통 😐' : '높음 😰'}
                  </div>
                </div>
                <div className="relative">
                  <div className="flex items-center mb-1">
                    <div className="text-xs text-gray-500">커버 소요 기간</div>
                    <button
                      className="ml-1 relative"
                      onMouseEnter={() => setShowTooltip('coverDays')}
                      onMouseLeave={() => setShowTooltip(null)}
                    >
                      <Info size={12} className="text-gray-400 hover:text-gray-600" />
                      {showTooltip === 'coverDays' && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-52 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                          <div className="mb-1 font-semibold">⏱️ 커버 소요 기간 기준</div>
                          <div>• 3일 미만: 빠른 회복</div>
                          <div>• 3~7일: 보통</div>
                          <div>• 7일 이상: 오래 걸림</div>
                          <div className="mt-1 text-gray-300">* 공매도를 모두 갚는데 걸리는 시간</div>
                        </div>
                      )}
                    </button>
                  </div>
                  <div className={`text-lg font-bold ${
                    (shortPositionData?.shortRatio || 0) < 3 ? 'text-green-500' :
                    (shortPositionData?.shortRatio || 0) < 7 ? 'text-yellow-500' : 'text-red-500'
                  }`}>
                    {shortPositionData?.shortRatio?.toFixed(1) || '-'}일
                  </div>
                  <div className="text-xs text-gray-400">
                    {(shortPositionData?.shortRatio || 0) < 3 ? '빠른 회복 😊' :
                     (shortPositionData?.shortRatio || 0) < 7 ? '보통 😐' : '오래 걸림 😰'}
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center">
                    <span className="text-xs text-gray-500">공매도 위험도</span>
                    <button
                      className="ml-1 relative"
                      onMouseEnter={() => setShowTooltip('riskLevel')}
                      onMouseLeave={() => setShowTooltip(null)}
                    >
                      <Info size={12} className="text-gray-400 hover:text-gray-600" />
                      {showTooltip === 'riskLevel' && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                          <div className="mb-1 font-semibold">🚨 공매도 위험도란?</div>
                          <div>• 안전: 공매도가 적어 주가 안정</div>
                          <div>• 주의: 공매도가 보통 수준</div>
                          <div>• 위험: 공매도가 많아 하락 압력</div>
                          <div className="mt-1 text-gray-300">* 공매도가 많을수록 주가 하락 리스크 증가</div>
                        </div>
                      )}
                    </button>
                  </div>
                  <span className={`text-sm font-bold ${
                    (shortPositionData?.shortPercentFloat || 0) < 0.05 ? 'text-green-500' :
                    (shortPositionData?.shortPercentFloat || 0) < 0.15 ? 'text-yellow-500' : 'text-red-500'
                  }`}>
                    {(shortPositionData?.shortPercentFloat || 0) < 0.05 ? '안전' :
                     (shortPositionData?.shortPercentFloat || 0) < 0.15 ? '주의' : '위험'}
                  </span>
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full">
                  <div 
                    className={`h-full rounded-full ${
                      (shortPositionData?.shortPercentFloat || 0) < 0.05 ? 'bg-green-500' :
                      (shortPositionData?.shortPercentFloat || 0) < 0.15 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{
                      width: `${Math.min((shortPositionData?.shortPercentFloat || 0) * 100 * 4, 100)}%`
                    }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>안전</span>
                  <span>위험</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="text-gray-500">지난달 대비</div>
                  <div className={`font-semibold ${
                    (shortPositionData?.shortChangePercent || 0) < 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {shortPositionData?.shortChangePercent ? 
                      `${shortPositionData.shortChangePercent > 0 ? '+' : ''}${shortPositionData.shortChangePercent.toFixed(1)}%` : '-'}
                  </div>
                  <div className="text-gray-400">
                    {(shortPositionData?.shortChangePercent || 0) < 0 ? '감소 (좋음)' : '증가 (나쁨)'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">공매도 주식 수</div>
                  <div className="font-semibold">
                    {shortPositionData?.sharesShort ? 
                      `${(shortPositionData.sharesShort / 10000).toFixed(1)}` : '-'}
                  </div>
                  <div className="text-gray-400">
                    단위 - 만주
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        

      {/* 투자 면책 조항 */}
      <InvestmentDisclaimer />
    </div>

      <FullAnalysisModal 
        isOpen={isFullAnalysisModalOpen}
        onClose={() => setIsFullAnalysisModalOpen(false)}
        stockSymbol={stockName}
        // analysisText=""
        reportType={currentReportType}
      />
      
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={closeSearchModal}
        onStockClick={handleStockClick}
      />

      <ContactFooter />
      <AlertModal alert={alert} onClose={closeAlert} />
      </div>

  );
};

export default StockDetailPage;